import React, { useState, useEffect } from 'react';

const scoreColor = s => s >= 4?'#2E7D32':s >= 3?'#1565C0':s >= 2?'#F57F17':'#C62828';
const barColor   = s => s >= 4?'#4CAF50':s >= 3?'#1565C0':s >= 2?'#FFA000':'#EF5350';
const deltaArrow = d => d > 0 ? '▲' : d < 0 ? '▼' : '—';
const deltaColor = d => d > 0 ? '#2E7D32' : d < 0 ? '#C62828' : '#B0BAC5';

const SCORING_SCALE = [
  { level:1, name:'Initial',    color:'#C62828', bg:'#FFEBEE', desc:'Control absent or ad-hoc. No documentation, no ownership, no consistent application.' },
  { level:2, name:'Developing', color:'#F57F17', bg:'#FFF8E1', desc:'Control exists informally. Partial documentation. Not consistently applied across the organisation.' },
  { level:3, name:'Defined',    color:'#1565C0', bg:'#E8F0FE', desc:'Control documented, approved, and communicated. Applied consistently. Ownership assigned.' },
  { level:4, name:'Managed',    color:'#2E7D32', bg:'#E8F5E9', desc:'Control measured, monitored, and reviewed regularly. Evidence of continuous improvement.' },
  { level:5, name:'Optimized',  color:'#1565C0', bg:'#E3F2FD', desc:'Control automated, integrated into organisational culture, and benchmarked externally.' },
];

function deriveUpgradePath(currentLicensing) {
  if (!currentLicensing) return { current: 'Unknown', upgrades: ['E5 Security', 'E5 Compliance', 'E5 Identity'], summary: 'Full E5 suite recommended' };
  const cl = currentLicensing.toLowerCase();
  if (cl.includes('e5') && !cl.includes('mixed')) return { current: currentLicensing, upgrades: [], summary: 'Already on E5 — ensure add-ons are activated (Defender for AI, Purview AI Hub, Entra Agent ID)' };
  if (cl.includes('mixed')) return { current: currentLicensing, upgrades: ['Standardize to full E5'], summary: 'Standardize mixed E3/E5 to full E5 for consistent AI governance coverage' };
  if (cl.includes('e3')) return { current: currentLicensing, upgrades: ['E5 Security', 'E5 Compliance', 'E5 Identity'], summary: 'E3 → E5 upgrade required for AI security controls' };
  if (cl.includes('business premium')) return { current: currentLicensing, upgrades: ['E5 Security', 'E5 Compliance'], summary: 'Business Premium covers basics — E5 needed for enterprise AI governance' };
  if (cl.includes('business standard')) return { current: currentLicensing, upgrades: ['Business Premium or E3', 'E5 Security', 'E5 Compliance'], summary: 'Business Standard lacks security baseline — significant upgrade needed' };
  return { current: currentLicensing, upgrades: ['E5 Security', 'E5 Compliance', 'E5 Identity'], summary: 'E5 suite recommended for full SAARA remediation coverage' };
}

export default function Report({ API, engagement }) {
  const [data, setData] = useState(null);
  const [delta, setDelta] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState('');
  const [showMethodology, setShowMethodology] = useState(false);
  const [openDomains, setOpenDomains] = useState({});
  const [controlDetail, setControlDetail] = useState({});

  const clientName  = engagement?.orgName      || 'Contoso Financial Services';
  const assessor    = engagement?.assessor     || 'Abbas Ali Shaikh';
  const engDate     = engagement?.engDate
    ? new Date(engagement.engDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'April 2026';
  const office      = engagement?.office       || 'Malaysia';
  const industry    = engagement?.industry     || 'Banking & Financial Services';
  const country     = engagement?.country      || 'Malaysia';
  const licensing   = engagement?.microsoftLicensing || engagement?.licensing || 'M365 E3';
  const contactName = engagement?.contactName  || '';
  const contactEmail= engagement?.contactEmail || '';
  const engType     = engagement?.engType      || 'New Assessment';
  const engName     = engagement?.engName      || 'SAARA Assessment';

  const upgradePath = deriveUpgradePath(licensing);

  useEffect(() => {
    fetch(`${API}/api/report`).then(r => r.json()).then(d => {
      setData(d);
      if (d.recertification_summary && d.recertification_summary.is_recertification && d.engagement?.id) {
        fetch(`${API}/api/delta/${d.engagement.id}`).then(r => r.json()).then(setDelta).catch(() => {});
      }
    });
  }, [API]);

  const toggleDomain = async (domainId) => {
    const isOpen = openDomains[domainId];
    setOpenDomains(p => ({...p, [domainId]: !isOpen}));
    if (!isOpen && !controlDetail[domainId]) {
      const ctrls = await fetch(`${API}/api/controls?domain=${domainId}`).then(r => r.json());
      setControlDetail(p => ({...p, [domainId]: ctrls}));
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    setExportMsg('Generating PDF report...');
    try {
      const resp = await fetch(`${API}/api/export/pdf?client=${encodeURIComponent(clientName)}`);
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = resp.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="(.+?)"/);
      a.download = match ? match[1] : `SAARA_FindingsReport_${clientName.replace(/\s+/g,'_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExportMsg('Report downloaded');
      setTimeout(() => setExportMsg(''), 4000);
    } catch (e) {
      setExportMsg('Export failed — check backend');
      setTimeout(() => setExportMsg(''), 4000);
    }
    setExporting(false);
  };

  const sendToClient = () => {
    alert(contactEmail
      ? `Report will be sent to ${contactName} (${contactEmail})`
      : 'No contact email on file — add contact details in the engagement setup');
  };

  if (!data) return <div className="empty-state">Loading report...</div>;

  const maturityLabel = s => {
    if (s >= 4.5) return 'Optimized';
    if (s >= 3.5) return 'Managed';
    if (s >= 2.5) return 'Defined';
    if (s >= 1.5) return 'Developing';
    return 'Initial';
  };

  const statusPill = s => {
    if (s === 'provided') return <span className="pill pill-green">Compliant</span>;
    if (s === 'partial')  return <span className="pill pill-amber">Partial</span>;
    if (s === 'missing')  return <span className="pill pill-red">Gap</span>;
    return <span className="pill pill-gray">Pending</span>;
  };

  const recert = data.recertification_summary;
  const isRecert = recert && recert.is_recertification;

  return (
    <div>

      {/* ── Report header ── */}
      <div className="report-header" style={{marginBottom:20}}>
        <div>
          <div style={{fontSize:15,fontWeight:600,color:'#0D1B2A'}}>
            SAARA Assessment Report
            {isRecert && <span style={{marginLeft:10,fontSize:11,padding:'3px 10px',borderRadius:10,background:'#E8F5E9',color:'#2E7D32',fontWeight:500}}>Recertification — Run {recert.run_number}</span>}
          </div>
          <div style={{fontSize:11,color:'#7A8899',marginTop:3}}>
            {clientName} &nbsp;·&nbsp; {engDate} &nbsp;·&nbsp; {assessor}, Kloudynet Technologies ({office})
            {engType !== 'New Assessment' && (
              <span className="pill pill-blue" style={{marginLeft:8,fontSize:9}}>{engType}</span>
            )}
          </div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {exportMsg && (
            <span style={{
              fontSize:11, padding:'4px 12px', borderRadius:10,
              background: exportMsg.startsWith('Report down') ? 'rgba(76,175,80,0.2)' : 'rgba(255,152,0,0.2)',
              color: exportMsg.startsWith('Report down') ? '#4CAF50' : '#FFA000',
            }}>{exportMsg}</span>
          )}
          <button className="btn-secondary" onClick={exportPDF} disabled={exporting} style={{opacity:exporting?0.6:1}}>
            {exporting ? 'Generating...' : 'Export PDF'}
          </button>
          <button className="btn-secondary" onClick={sendToClient}>Send to client</button>
        </div>
      </div>

      {/* ── Engagement details strip ── */}
      <div style={{
        background:'#fff', border:'1px solid #E8ECF0', borderRadius:8,
        padding:'10px 16px', marginBottom:16,
        display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12,
      }}>
        {[
          ['Client', clientName], ['Industry', industry], ['Country', country], ['Current licensing', licensing],
          ['Engagement', engName], ['Type', engType], ['Lead assessor', assessor], ['Contact', contactName || '—'],
        ].map(([k, v]) => (
          <div key={k}>
            <div style={{fontSize:9,color:'#B0BAC5',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:2}}>{k}</div>
            <div style={{fontSize:11,fontWeight:500,color:'#0D1B2A'}}>{v}</div>
          </div>
        ))}
      </div>

      {/* ── Assessment Methodology (collapsible) ── */}
      <div style={{marginBottom:16}}>
        <button className="btn-ghost" onClick={() => setShowMethodology(p => !p)} style={{fontSize:11,marginBottom:showMethodology?8:0}}>
          {showMethodology ? 'Hide assessment methodology' : 'Assessment methodology & scoring scale'}
        </button>
        {showMethodology && (
          <div className="card">
            <div className="card-header">
              <div className="card-header-title">Assessment methodology</div>
              <div className="card-header-sub">SAARA v2.0 — Kloudynet Technologies</div>
            </div>
            <div className="card-body">
              {/* Frameworks */}
              <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:6}}>Framework basis</div>
              <div style={{fontSize:11,color:'#455A64',lineHeight:1.6,marginBottom:14}}>
                This assessment is built on four internationally recognised frameworks: <strong>NIST AI RMF 1.0</strong> (AI risk management functions: Govern, Map, Measure, Manage), <strong>ISO/IEC 42001:2023</strong> (AI management system requirements and Annex A controls), <strong>OWASP LLM Top 10 (2025)</strong> (application security risks specific to large language models), and <strong>MITRE ATLAS v4.0</strong> (adversarial threat landscape for AI systems).
              </div>

              {/* Scoring scale */}
              <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>Maturity scoring scale</div>
              <div className="tbl-wrap" style={{marginBottom:14}}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{width:50}}>Level</th>
                      <th style={{width:90}}>Maturity</th>
                      <th>Definition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SCORING_SCALE.map(s => (
                      <tr key={s.level}>
                        <td><span style={{fontSize:14,fontWeight:700,color:s.color}}>{s.level}</span></td>
                        <td><span style={{fontSize:11,fontWeight:600,color:s.color,padding:'2px 8px',borderRadius:8,background:s.bg}}>{s.name}</span></td>
                        <td style={{fontSize:11,color:'#455A64'}}>{s.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Evidence layers */}
              <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>Evidence collection layers</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:14}}>
                {[
                  { layer:'Layer 1', title:'Automated', desc:'Microsoft Graph API, Sentinel KQL, Defender for Cloud, Purview DSPM, Entra ID', color:'#2E7D32', bg:'#E8F5E9' },
                  { layer:'Layer 2', title:'AI-assisted', desc:'Document review via Azure OpenAI — structured scoring with assessor confirmation', color:'#6A1B9A', bg:'#F3E5F5' },
                  { layer:'Layer 3', title:'Assessor', desc:'Stakeholder interviews, manual observation, professional judgment', color:'#455A64', bg:'#F1F3F5' },
                ].map(l => (
                  <div key={l.layer} style={{background:l.bg,borderRadius:8,padding:'10px 12px'}}>
                    <div style={{fontSize:10,fontWeight:600,color:l.color,marginBottom:2}}>{l.layer} — {l.title}</div>
                    <div style={{fontSize:10,color:'#455A64',lineHeight:1.4}}>{l.desc}</div>
                  </div>
                ))}
              </div>

              {/* Scope */}
              <div style={{fontSize:11,color:'#7A8899',lineHeight:1.5}}>
                <strong>Scope:</strong> 55 controls across 6 domains. Target maturity: 3.0 (Defined) across all domains. Level 3+ across all domains builds 70–80% of the ISO 42001:2023 certification evidence base.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Recertification comparison banner ── */}
      {isRecert && (
        <div style={{
          background:'linear-gradient(135deg,#E8F5E9 0%,#E3F2FD 100%)',
          border:'1px solid #A5D6A7', borderRadius:10, padding:18, marginBottom:20,
        }}>
          <div style={{fontSize:13,fontWeight:600,color:'#1B5E20',marginBottom:12}}>
            Recertification comparison — Run {recert.run_number} vs Baseline
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:14}}>
            {[
              { label:'Baseline score', val:recert.overall_baseline?.toFixed(1), color:'#7A8899' },
              { label:'Current score', val:recert.overall_current?.toFixed(1), color:scoreColor(recert.overall_current||0) },
              { label:'Overall delta', val:`${recert.overall_delta>0?'+':''}${recert.overall_delta?.toFixed(1)}`, color:deltaColor(recert.overall_delta||0) },
              { label:'Controls improved', val:recert.controls_improved, color:'#2E7D32' },
              { label:'Controls regressed', val:recert.controls_regressed, color:recert.controls_regressed>0?'#C62828':'#2E7D32' },
            ].map((s,i) => (
              <div key={i} style={{background:'#fff',border:'1px solid #C8E6C9',borderRadius:8,padding:'10px 12px',textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:700,color:s.color}}>{s.val}</div>
                <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>{s.label}</div>
              </div>
            ))}
          </div>
          {recert.domains_improved?.length > 0 && <div style={{fontSize:11,color:'#2E7D32',marginBottom:4}}>Domains improved: {recert.domains_improved.join(', ')}</div>}
          {recert.domains_regressed?.length > 0 && <div style={{fontSize:11,color:'#C62828'}}>Domains regressed: {recert.domains_regressed.join(', ')}</div>}
          {recert.baseline_date && <div style={{fontSize:10,color:'#7A8899',marginTop:6}}>Baseline date: {new Date(recert.baseline_date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</div>}
        </div>
      )}

      {/* ── Stat cards ── */}
      <div className="stat-grid" style={{marginBottom:20}}>
        <div className="stat-card">
          <div className="stat-val" style={{color:scoreColor(data.overall_score)}}>{data.overall_score}</div>
          <div className="stat-lbl">Overall maturity</div>
          <div className="stat-sub">{maturityLabel(data.overall_score)} — Target: 3.0 (Defined)</div>
          {isRecert && <div style={{fontSize:10,color:deltaColor(recert.overall_delta),fontWeight:600,marginTop:4}}>{deltaArrow(recert.overall_delta)} {recert.overall_delta>0?'+':''}{recert.overall_delta?.toFixed(1)} since baseline</div>}
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{color:'#C62828'}}>{data.total_gaps}</div>
          <div className="stat-lbl">Total gaps identified</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{color:'#1565C0'}}>{data.scored}</div>
          <div className="stat-lbl">Controls scored</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{color:'#F57F17'}}>{55 - data.scored}</div>
          <div className="stat-lbl">Pending evidence</div>
        </div>
      </div>

      {/* ── Domain scorecard + SKU ── */}
      <div className="report-grid">
        <div className="card">
          <div className="card-header">
            <div className="card-header-title">Domain maturity scorecard</div>
            <div className="card-header-sub">{isRecert ? 'Current vs baseline — grey marker shows baseline score' : 'Current vs target (3.0) — grey marker shows target'}</div>
          </div>
          <div className="card-body">
            {data.domain_scores.map(d => {
              const pct = Math.round((d.score / 5) * 100);
              const hasDelta = d.baseline_score != null && d.delta != null;
              const refPct = hasDelta ? Math.round((d.baseline_score / 5) * 100) : Math.round((d.target / 5) * 100);
              return (
                <div className="score-row" key={d.id}>
                  <div className="score-domain">
                    <span className="score-domain-tag">{d.id}</span>
                    <span style={{fontSize:11,color:'#455A64'}}>{d.name}</span>
                  </div>
                  <div style={{width:110,position:'relative'}}>
                    <div className="score-bar-wrap" style={{width:110}}>
                      <div className="score-bar-fill" style={{width:`${pct}%`,background:barColor(d.score)}}/>
                    </div>
                    <div style={{position:'absolute',top:-3,left:`${refPct}%`,width:2,height:12,background:'#9E9E9E',borderRadius:1}} title={hasDelta ? `Baseline: ${d.baseline_score.toFixed(1)}` : 'Target: 3.0'}/>
                  </div>
                  <div className="score-num" style={{color:scoreColor(d.score)}}>
                    {d.score.toFixed(1)}
                    {hasDelta
                      ? <span style={{fontSize:10,color:deltaColor(d.delta),marginLeft:4}}>{deltaArrow(d.delta)}{d.delta>0?'+':''}{d.delta.toFixed(1)}</span>
                      : <span style={{fontSize:10,color:'#B0BAC5'}}>→3</span>}
                  </div>
                </div>
              );
            })}
            <div style={{marginTop:12,fontSize:10,color:'#B0BAC5',borderTop:'1px solid #EEF0F3',paddingTop:8}}>
              {isRecert ? 'Grey marker = baseline (Run 1) · Green ▲ = improved · Red ▼ = regressed' : 'Grey marker = target (3.0) · Scale: 1 (Initial) → 5 (Optimized)'}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-header-title">Microsoft licensing roadmap</div>
            <div className="card-header-sub">SKU recommendations based on SAARA findings and current licensing</div>
          </div>
          <div className="card-body">
            <div style={{background:'#F5F7F9',border:'1px solid #E0E4E8',borderRadius:8,padding:'10px 14px',marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A'}}>Current licensing</div>
                <span className="pill pill-blue">{upgradePath.current}</span>
              </div>
              <div style={{fontSize:11,color:'#455A64',marginBottom:6}}>{upgradePath.summary}</div>
              {upgradePath.upgrades.length > 0 && (
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {upgradePath.upgrades.map((u,i) => (
                    <span key={i} style={{fontSize:10,padding:'3px 8px',borderRadius:6,background:'#FFF3E0',color:'#E65100',fontWeight:500,border:'1px solid #FFE0B2'}}>+ {u}</span>
                  ))}
                </div>
              )}
            </div>
            {data.skus.map((s, i) => (
              <div key={i} style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',padding:'8px 0',borderBottom:i<data.skus.length-1?'1px solid #F0F2F5':'none'}}>
                <div style={{flex:1,marginRight:10}}>
                  <div style={{fontSize:12,fontWeight:500,color:'#0D1B2A'}}>{s.product}</div>
                  <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>{s.domain}: {s.gap}</div>
                  <div style={{fontSize:10,color:'#455A64',marginTop:2,fontStyle:'italic'}}>{s.value}</div>
                </div>
                <span className="pill pill-blue" style={{whiteSpace:'nowrap'}}>{s.licensing}</span>
              </div>
            ))}
            <div className="info-box info-box-amber" style={{marginTop:12}}>
              <strong>Current:</strong> {licensing} &nbsp;→&nbsp; <strong>Required:</strong> {upgradePath.upgrades.length > 0 ? upgradePath.upgrades.join(' + ') : 'Current licensing sufficient — activate add-ons'}
            </div>
          </div>
        </div>
      </div>

      {/* ── Recertification delta table ── */}
      {isRecert && delta && delta.controls && (
        <div className="card" style={{marginTop:16}}>
          <div className="card-header">
            <div className="card-header-title">Control-level changes since baseline</div>
            <div className="card-header-sub">{delta.controls_improved} improved · {delta.controls_regressed} regressed · {delta.controls_unchanged} unchanged</div>
          </div>
          <div className="tbl-wrap" style={{border:'none',borderRadius:0}}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{width:80}}>Control</th>
                  <th style={{width:60}}>Domain</th>
                  <th>Category</th>
                  <th style={{width:70}}>Baseline</th>
                  <th style={{width:70}}>Current</th>
                  <th style={{width:60}}>Delta</th>
                  <th style={{width:80}}>Direction</th>
                </tr>
              </thead>
              <tbody>
                {delta.controls.map((c, i) => (
                  <tr key={i} style={{background: c.delta < 0 ? 'rgba(198,40,40,0.04)' : c.delta > 0 ? 'rgba(46,125,50,0.04)' : 'transparent'}}>
                    <td><span style={{fontSize:11,fontWeight:600,color:'#1565C0'}}>{c.control_id}</span></td>
                    <td><span style={{fontSize:10,color:'#7A8899'}}>{c.domain}</span></td>
                    <td style={{fontSize:11,color:'#455A64'}}>{c.category}</td>
                    <td style={{fontWeight:500,color:'#7A8899'}}>{c.baseline_score}/5</td>
                    <td style={{fontWeight:600,color:scoreColor(c.current_score)}}>{c.current_score}/5</td>
                    <td style={{fontWeight:700,color:deltaColor(c.delta)}}>{c.delta > 0 ? '+' : ''}{c.delta}</td>
                    <td>
                      <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,fontWeight:500,
                        background: c.direction==='improved'?'#E8F5E9':c.direction==='regressed'?'#FFEBEE':'#F5F5F5',
                        color: c.direction==='improved'?'#2E7D32':c.direction==='regressed'?'#C62828':'#9E9E9E',
                      }}>{deltaArrow(c.delta)} {c.direction}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Priority findings ── */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header">
          <div className="card-header-title">Priority findings & recommended actions</div>
          <div className="card-header-sub">Ordered by risk severity — P1: remediate within 30 days · P2: remediate within 90 days</div>
        </div>
        <div className="tbl-wrap" style={{border:'none',borderRadius:0}}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{width:50}}>Priority</th>
                <th style={{width:80}}>Control</th>
                <th>Finding</th>
                <th style={{width:100}}>Source</th>
                <th style={{width:50}}>Score</th>
                <th style={{width:70}}>Timeline</th>
              </tr>
            </thead>
            <tbody>
              {data.priority_findings.map((f, i) => (
                <tr key={i}>
                  <td><span className={`pill pill-${f.priority==='P1'?'p1':'p2'}`}>{f.priority}</span></td>
                  <td>
                    <span style={{fontSize:11,fontWeight:600,color:'#1565C0'}}>{f.control_id}</span>
                    <br/><span style={{fontSize:10,color:'#7A8899'}}>{f.category}</span>
                  </td>
                  <td style={{fontSize:11,color:'#455A64'}}>{f.statement}</td>
                  <td>
                    {f.source==='Manual'?<span className="chip chip-manual">{f.source}</span>:
                     f.source.includes('KQL')||f.source==='Sentinel'?<span className="chip chip-kql">{f.source}</span>:
                     <span className="chip chip-ms">{f.source}</span>}
                  </td>
                  <td style={{fontWeight:600,color:scoreColor(f.score)}}>{f.score}/5</td>
                  <td style={{fontSize:10,fontWeight:500,color:f.priority==='P1'?'#C62828':'#E65100'}}>{f.priority==='P1'?'30 days':'90 days'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Domain control detail (expandable) ── */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header">
          <div className="card-header-title">Domain control detail</div>
          <div className="card-header-sub">All 55 controls with status, score, assessor notes, and evidence source — click domain to expand</div>
        </div>
        <div className="card-body" style={{padding:0}}>
          {data.domain_scores.map(d => {
            const isOpen = openDomains[d.id];
            const ctrls = controlDetail[d.id] || [];
            return (
              <div key={d.id} style={{borderBottom:'1px solid #EEF0F3'}}>
                <div onClick={() => toggleDomain(d.id)} style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'10px 16px', cursor:'pointer', transition:'background 0.15s',
                  background: isOpen ? '#F5F7F9' : 'transparent',
                }}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <span style={{fontSize:10,fontWeight:600,color:scoreColor(d.score),background:d.score>=3?'#E8F5E9':d.score>=2?'#FFF8E1':'#FFEBEE',padding:'2px 6px',borderRadius:6}}>{d.id}</span>
                    <span style={{fontSize:12,fontWeight:500,color:'#0D1B2A'}}>{d.name}</span>
                    <span style={{fontSize:11,fontWeight:600,color:scoreColor(d.score)}}>{d.score.toFixed(1)}/5</span>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:10,color:'#B0BAC5'}}>{d.gap_count} gap{d.gap_count!==1?'s':''}</span>
                    <span style={{fontSize:10,color:'#B0BAC5',transition:'transform 0.2s',transform:isOpen?'rotate(90deg)':'none'}}>▶</span>
                  </div>
                </div>
                {isOpen && ctrls.length > 0 && (
                  <div style={{padding:'0 8px 8px'}}>
                    <table className="tbl" style={{fontSize:11}}>
                      <thead>
                        <tr>
                          <th style={{width:70}}>Control</th>
                          <th style={{width:100}}>Category</th>
                          <th>Statement</th>
                          <th style={{width:80}}>Source</th>
                          <th style={{width:65}}>Status</th>
                          <th style={{width:45}}>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ctrls.map(c => (
                          <React.Fragment key={c.id}>
                            <tr>
                              <td>
                                <span style={{fontSize:10,fontWeight:600,color:'#1565C0'}}>{c.id}</span>
                                {(c.sub_controls||[]).length > 0 && <div style={{fontSize:8,color:'#0078D4',fontWeight:500}}>+{c.sub_controls.length} sub</div>}
                              </td>
                              <td style={{fontSize:10,color:'#7A8899'}}>{c.category}</td>
                              <td style={{fontSize:10,color:'#455A64',lineHeight:1.3}}>{c.statement}</td>
                              <td style={{fontSize:10}}>{c.source==='Manual'?<span className="chip chip-manual">{c.source}</span>:<span className="chip chip-ms">{c.source}</span>}</td>
                              <td>{statusPill(c.status)}</td>
                              <td style={{fontWeight:600,color:c.score!=null?scoreColor(c.score):'#B0BAC5'}}>{c.score!=null?`${c.score}/5`:'—'}</td>
                            </tr>
                            {c.assessor_notes && (
                              <tr>
                                <td colSpan={6} style={{padding:'4px 12px 8px',background:'#F8F9FA',borderBottom:'1px solid #EEF0F3'}}>
                                  <span style={{fontSize:9,color:'#B0BAC5',textTransform:'uppercase',letterSpacing:'0.04em'}}>Assessor notes: </span>
                                  <span style={{fontSize:10,color:'#455A64'}}>{c.assessor_notes}</span>
                                </td>
                              </tr>
                            )}
                            {(c.sub_controls||[]).map(sc => (
                              <tr key={sc.id} style={{background:'#FAFBFD'}}>
                                <td style={{paddingLeft:16}}><span style={{fontSize:9,fontWeight:600,color:'#0078D4'}}>{sc.id}</span></td>
                                <td style={{fontSize:9,color:'#7A8899'}}>{c.category}</td>
                                <td style={{fontSize:9,color:'#455A64',lineHeight:1.3}}>
                                  {sc.statement}
                                  <div style={{fontSize:8,color:'#7A8899',marginTop:2}}>Evidence: {sc.evidence_required}</div>
                                </td>
                                <td colSpan={3} style={{fontSize:8,color:'#7A8899',fontStyle:'italic'}}>Scored with {c.id}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {isOpen && ctrls.length === 0 && (
                  <div style={{padding:'12px 16px',fontSize:11,color:'#B0BAC5'}}>Loading controls...</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── PDF export sections ── */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div className="card-header-title">Full report — PDF export</div>
            <div className="card-header-sub">{clientName} · {engDate} · {assessor}, Kloudynet Technologies</div>
          </div>
          <button className="btn-primary" onClick={exportPDF} disabled={exporting} style={{opacity:exporting?0.6:1}}>
            {exporting ? 'Generating...' : 'Export full PDF report'}
          </button>
        </div>
        <div className="card-body">
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            {[
              ['1','Assessment Methodology','Frameworks, scoring scale (1–5), evidence collection approach, scope & limitations'],
              ['2','Executive Summary','Overall maturity, key findings, top risks, key recommendations'],
              ['3','Domain Maturity Scorecard',isRecert ? 'Current vs baseline scores with delta for all 6 domains' : 'Current vs target scores for all 6 domains'],
              ['4','Priority Findings','P1/P2 findings with recommended actions, framework mapping, remediation timelines'],
              ['5','Domain Control Detail','All 55 controls with status, source, assessor notes, provenance, and framework references'],
              ['6','Microsoft Licensing Roadmap',`Current: ${licensing} — SKU upgrade path with business case`],
              ['7','Remediation Roadmap','Dynamic 12-month plan driven by actual gap data with action IDs'],
              ...(isRecert ? [['8','Recertification Comparison','Baseline vs current scores, domain and control-level deltas']] : []),
              [isRecert?'9':'8','Framework Reference','NIST AI RMF 1.0, ISO 42001:2023, OWASP LLM Top 10, MITRE ATLAS mapping'],
              [isRecert?'10':'9','Assessment Attestation','Assessor and client sign-off with professional attestation'],
            ].map(([num, title, desc]) => (
              <div key={num+title} style={{display:'flex',gap:10,padding:'8px 0',borderBottom:'1px solid #F0F2F5'}}>
                <div style={{width:22,height:22,borderRadius:'50%',background:'#E8F0FE',color:'#1565C0',fontSize:11,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{num}</div>
                <div>
                  <div style={{fontSize:12,fontWeight:500,color:'#0D1B2A'}}>{title}</div>
                  <div style={{fontSize:10,color:'#7A8899',marginTop:1}}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Sign-off / Attestation ── */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header">
          <div className="card-header-title">Assessment attestation</div>
          <div className="card-header-sub">Professional attestation and client acknowledgement</div>
        </div>
        <div className="card-body">
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,marginBottom:20}}>
            {/* Assessor */}
            <div style={{borderRight:'1px solid #EEF0F3',paddingRight:24}}>
              <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:12}}>Lead Assessor</div>
              <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'6px 12px',fontSize:11}}>
                <span style={{color:'#7A8899'}}>Name:</span>
                <span style={{fontWeight:500,color:'#0D1B2A'}}>{assessor}</span>
                <span style={{color:'#7A8899'}}>Organisation:</span>
                <span style={{fontWeight:500,color:'#0D1B2A'}}>Kloudynet Technologies ({office})</span>
                <span style={{color:'#7A8899'}}>Date:</span>
                <span style={{fontWeight:500,color:'#0D1B2A'}}>{engDate}</span>
              </div>
              <div style={{marginTop:16,borderBottom:'1px solid #0D1B2A',width:'80%',height:1}}/>
              <div style={{fontSize:10,color:'#B0BAC5',marginTop:4}}>Signature</div>
            </div>

            {/* Client */}
            <div>
              <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:12}}>Client Representative</div>
              <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'6px 12px',fontSize:11}}>
                <span style={{color:'#7A8899'}}>Name:</span>
                <span style={{fontWeight:500,color:'#0D1B2A'}}>{contactName || '—'}</span>
                <span style={{color:'#7A8899'}}>Organisation:</span>
                <span style={{fontWeight:500,color:'#0D1B2A'}}>{clientName}</span>
                <span style={{color:'#7A8899'}}>Date:</span>
                <span style={{fontWeight:500,color:'#0D1B2A'}}>{engDate}</span>
              </div>
              <div style={{marginTop:16,borderBottom:'1px solid #0D1B2A',width:'80%',height:1}}/>
              <div style={{fontSize:10,color:'#B0BAC5',marginTop:4}}>Signature</div>
            </div>
          </div>

          <div style={{fontSize:10,color:'#7A8899',lineHeight:1.6,padding:'12px 14px',background:'#F8F9FA',borderRadius:6,borderLeft:'3px solid #B0BAC5'}}>
            This report represents the professional judgment of the lead assessor based on evidence reviewed, stakeholder interviews conducted, and automated data collection performed during the assessment period. Scores reflect the organisation's AI security maturity at the time of assessment and may change as additional evidence is provided or controls are implemented. This report is confidential and intended for the named client organisation and Kloudynet Technologies only.
          </div>
        </div>
      </div>

    </div>
  );
}
